/**
 * Crossfilter Manager — coordonează filtrele interactive.
 * 
 * Menține selecțiile active din diferite surse (histograme, hartă)
 * și emite un eveniment 'crossfilter-changed' la fiecare modificare.
 * Toate componentele (histograme, hartă, panou statistici) ascultă acest eveniment.
 */
import { query, getPopulationMap, getGridMeta } from './sqlEngine.js';

// ── State ────────────────────────────────────────────────────────

/** Selecții active: { source: { variable, min, max, ... } } */
const selections = new Map();

/** Rezultatele ultimului crossfilter (cache) */
let lastResult = null;

/** Callback-uri înregistrate pentru schimbări crossfilter */
const listeners = [];

/** Listeners pentru start calcul filtre (loading state) */
const startListeners = [];

/** Variabilele disponibile în tabelul curent */
let registeredVariables = [];

// ── API public ───────────────────────────────────────────────────

/**
 * Setează variabilele disponibile (apelat la înregistrarea tabelului).
 */
export function setVariables(vars) {
  registeredVariables = vars;
}

/**
 * Adaugă o selecție (de la o histogramă sau hartă).
 * @param {string} source - ID-ul sursei (ex: 'histogram-pm25', 'map-brush')
 * @param {object} selection - { variable, min, max } sau { type: 'geo', ... }
 */
export function setSelection(source, selection) {
  if (!selection) {
    selections.delete(source);
  } else {
    selections.set(source, selection);
  }
  runCrossfilter();
}

/**
 * Șterge selecția unei surse.
 */
export function clearSelection(source) {
  selections.delete(source);
  runCrossfilter();
}

/**
 * Șterge toate selecțiile.
 */
export function clearAllSelections() {
  selections.clear();
  runCrossfilter();
}

/**
 * Înregistrează un listener pentru schimbări crossfilter.
 * @param {function(CrossfilterResult)} fn
 */
export function onCrossfilterChange(fn) {
  listeners.push(fn);
}

/**
 * Înregistrează un listener pentru start calcul filtre (loading state).
 * @param {function(object)} fn
 */
export function onCrossfilterStart(fn) {
  startListeners.push(fn);
}

/**
 * Returnează selecțiile active.
 */
export function getSelections() {
  return new Map(selections);
}

/**
 * Returnează ultimul rezultat crossfilter.
 */
export function getLastResult() {
  return lastResult;
}

/**
 * Verifică dacă există filtre active.
 */
export function hasActiveFilters() {
  return selections.size > 0;
}

// ── Internals ────────────────────────────────────────────────────

/**
 * Construiește clauza WHERE din selecțiile active.
 * @param {string} [excludeSource] - Sursă de exclus (pentru cross-filtering corect)
 */
function buildWhere(excludeSource = null) {
  const clauses = [];

  for (const [source, sel] of selections) {
    if (source === excludeSource) continue;

    if (sel.type === 'range' && sel.variable && sel.min != null && sel.max != null) {
      clauses.push(`${sel.variable} BETWEEN ${sel.min} AND ${sel.max}`);
    } else if (sel.type === 'geo' && sel.lonMin != null) {
      clauses.push(`lon BETWEEN ${sel.lonMin} AND ${sel.lonMax} AND lat BETWEEN ${sel.latMin} AND ${sel.latMax}`);
    } else if (sel.type === 'geo-circle' && sel.centerLon != null) {
      // Aproximare dreptunghiulară pentru performanță
      const dLon = sel.radiusDeg;
      const dLat = sel.radiusDeg;
      clauses.push(`lon BETWEEN ${sel.centerLon - dLon} AND ${sel.centerLon + dLon} AND lat BETWEEN ${sel.centerLat - dLat} AND ${sel.centerLat + dLat}`);
    }
  }

  return clauses.length > 0 ? `WHERE ${clauses.join(' AND ')}` : '';
}

/**
 * Rulează crossfilterul: execută query-uri SQL și emite rezultatul.
 */
async function runCrossfilter() {
  const where = buildWhere();
  const hasFilter = where.length > 0;

  // Emite eveniment de start (pentru afișare spinner / loading wheel)
  const startDetail = { hasFilter, selections: new Map(selections) };
  for (const fn of startListeners) {
    try { fn(startDetail); } catch (e) { console.error('[Crossfilter] Eroare start listener:', e); }
  }
  document.dispatchEvent(new CustomEvent('crossfilter-start', { detail: startDetail }));

  try {
    // 1. Statistici populație în selecție
    const statsSQL = `
      SELECT 
        COUNT(*) as cell_count,
        SUM(pop_total) as total_pop,
        SUM(pop_lt15) as pop_lt15,
        SUM(pop_15_64) as pop_15_64,
        SUM(pop_ge65) as pop_ge65,
        SUM(pop_female) as pop_female,
        SUM(pop_male) as pop_male,
        SUM(pop_employed) as pop_employed
      FROM cells ${where}
    `;

    // 2. ID-urile celulelor selectate (pentru mască hartă)
    const idsSQL = hasFilter
      ? `SELECT id FROM cells ${where}`
      : null;

    // 3. Medii variabile în selecție  
    const avgCols = registeredVariables
      .map(v => `AVG(${v}) as avg_${v}`)
      .join(', ');
    const avgSQL = `SELECT ${avgCols} FROM cells ${where}`;

    // Execută în paralel
    const [stats, avgs] = await Promise.all([
      query(statsSQL),
      avgCols ? query(avgSQL) : [{}],
    ]);

    let selectedIds = null;
    if (idsSQL) {
      const idsResult = await query(idsSQL);
      selectedIds = new Set(idsResult.map(r => Number(r.id)));
    }

    // Construiește rezultatul
    lastResult = {
      hasFilter,
      stats: stats[0] || {},
      averages: avgs[0] || {},
      selectedIds,
      selections: new Map(selections),
      timestamp: Date.now(),
    };

    // 4. Calculează histograme per variabilă (cu cross-filtering)
    lastResult.histograms = {};
    for (const v of registeredVariables) {
      const crossWhere = buildWhere(`histogram-${v}`);
      lastResult.histograms[v] = await computeHistogram(v, crossWhere);
    }

    // Emite la toți listenerii
    for (const fn of listeners) {
      try {
        fn(lastResult);
      } catch (e) {
        console.error('[Crossfilter] Eroare listener:', e);
      }
    }

    // Emit DOM event
    document.dispatchEvent(new CustomEvent('crossfilter-changed', { detail: lastResult }));

  } catch (err) {
    console.error('[Crossfilter] Eroare query:', err);
  }
}

/**
 * Calculează histograma pentru o variabilă.
 * @param {string} variable
 * @param {string} where - Clauza WHERE (cross-filtered)
 * @param {number} [nbins=20]
 */
async function computeHistogram(variable, where = '', nbins = 20) {
  try {
    // Obține min/max global (fără filtre)
    const [range] = await query(`
      SELECT MIN(${variable}) as vmin, MAX(${variable}) as vmax
      FROM cells WHERE ${variable} IS NOT NULL
    `);

    if (!range || range.vmin == null || range.vmax == null) {
      return { bins: [], min: 0, max: 0 };
    }

    const vmin = Number(range.vmin);
    const vmax = Number(range.vmax);
    const binWidth = (vmax - vmin) / nbins;

    if (binWidth <= 0) return { bins: [], min: vmin, max: vmax };

    // Histogramă cu SUM(pop_total) per bin
    const sql = `
      SELECT 
        FLOOR((${variable} - ${vmin}) / ${binWidth})::INTEGER as bin,
        COUNT(*) as count,
        SUM(pop_total) as pop
      FROM cells
      ${where ? where : `WHERE ${variable} IS NOT NULL`}
      ${where ? `AND ${variable} IS NOT NULL` : ''}
      GROUP BY bin
      ORDER BY bin
    `;

    const result = await query(sql);

    // Construiește bins complet (inclusiv cele goale)
    const bins = [];
    for (let i = 0; i < nbins; i++) {
      const row = result.find(r => Number(r.bin) === i);
      bins.push({
        binStart: vmin + i * binWidth,
        binEnd: vmin + (i + 1) * binWidth,
        count: row ? Number(row.count) : 0,
        pop: row ? Number(row.pop) : 0,
      });
    }

    return { bins, min: vmin, max: vmax, binWidth };
  } catch (e) {
    console.error(`[Crossfilter] Eroare histogramă ${variable}:`, e);
    return { bins: [], min: 0, max: 0 };
  }
}

/**
 * Forțează re-execuția crossfilterului (ex: la schimbarea timestep).
 */
export function refresh() {
  runCrossfilter();
}
